import { externalCoachFailureGuidance } from '../utils/externalCoachFailureGuidance';
import CoachMessageInput, { useCoachComposerSubmit } from './CoachMessageInput';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import CoachAvatar from './CoachAvatar';
import { getCoachImageVariantUrl } from '../utils/coachImageVariants';
import { buildCoachTimeline, coachInsightHasVisibleContent, getActivityEventPresentation, getInsightPresentation, getInsightTeaser } from './coachTimeline';
import { getCoachAskTypingSnapshot, getCoachThinkingSnapshot, publishCoachAskTyping, publishCoachHubOpen, subscribeCoachAskTyping, subscribeCoachThinking } from './activities/coachPresence';
import { buildCoachInsightActions } from './activities/coachFeedMessage';
import './CoachHubModal.css';
import StrategyRoadmap from './StrategyRoadmap';
import StrategyModal from './StrategyModal';
import { getStrategyTransitionAction, shouldOpenStrategyTransition } from './strategyTransitionAction';
import { CoachActionCards } from './CoachActionCard';
import {
    CoachStrategyProposalCard,
    createProposalCommentPrefill,
    getPendingStrategyProposals,
    presentStrategyProposal
} from './CoachStrategyProposalCard';
import ModalLoading from './ModalLoading';
import ExerciseCoachMessageAttachments from './activities/ExerciseCoachMessageAttachments';
import CoachMessageContent from './activities/CoachMessageContent';

import useDailyCoachTaskStatus from './activities/useDailyCoachTaskStatus';
import DailyCoachFailureNotice from './activities/DailyCoachFailureNotice';
import WorkoutCoachInlineThread from './WorkoutCoachInlineThread';
import CoachReactionChip from './CoachReactionChip';
import { coachSequenceNow, noteCoachCanonicalRead, noteCoachContextMatch, noteCoachOutputCommitted } from '../services/coachSequenceTiming';
import { shouldFetchInlineWorkoutThread, workoutThreadEventVersion } from './coachHubWorkoutThreadState';
import { countWorkoutProposalAnnotations } from './activities/workoutProposalAnnotations';
import { alignCoachTimelineTarget, resolveCoachTimelineFocusEntry, shouldAutoScrollCoachTimeline } from './coachHubScroll';
import { hasCoachReplyAfterMessage, mergePendingCoachMessage, shouldPreserveCoachMessageOnError } from './coachHubMessageReliability';
import { coachReplyIdentity, latestRegeneratableCoachReply } from './coachReplyRegeneration';

function DailyCoachFailureInChat({ backendService, onThinkingChange }) {
    const { pending, failed, status, retry, retrying, retryError, retryUncertain } = useDailyCoachTaskStatus(backendService);
    const noticeRef = useRef(null);
    const thinking = pending;
    useEffect(() => {
        onThinkingChange(thinking);
        return () => onThinkingChange(false);
    }, [thinking, onThinkingChange]);
    const publishedStatusId = status?.external_daily_task?.status === 'consumed'
        ? status?.external_daily_task?.insight?.status_context_id : null;
    useEffect(() => {
        if (!publishedStatusId) return;
        // The receipt is not a second message. Refresh the existing canonical
        // Coach Home timeline once this insight becomes durably published.
        window.dispatchEvent(new Event('coach-timeline-updated'));
    }, [publishedStatusId]);
    useEffect(() => {
        if (failed || retryUncertain) noticeRef.current?.scrollIntoView?.({ block: 'end' });
    }, [failed, retryUncertain]);
    return (failed || retryUncertain) && !thinking ? <div ref={noticeRef}><DailyCoachFailureNotice status={status} inChat onRetry={retry} retrying={retrying} retryError={retryError} retryUncertain={retryUncertain} /></div> : null;
}

// Compact timestamp for thread messages: shows the time, and the date too
// when the message isn't from today, so older history stays readable.
const formatMsgTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (sameDay) return time;
    return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${time}`;
};

/**
 * Coach hub — the single, chat-first home for everything the AI coach does.
 *
 * Layout:
 *   - Collapsible CONTEXT header (strategy snapshot · today's insight ·
 *     per-exercise memory) so the user can ground the conversation without
 *     the dashboard competing with the chat.
 *   - The conversation THREAD is the primary, persistent surface — every
 *     interaction (random chat, strategy/plan changes, recorded metrics,
 *     document check-ins) is a beat in this one thread.
 *   - A pinned COMPOSER (text + attachments).
 *
 * Props:
 *   - seedMessage / seedAttachments: when the user starts talking from the
 *     feed's inline reply, the first message is handed off here and posted
 *     into the real thread, so there is exactly one conversation.
 *   - exerciseScope: { exercise_id, exercise_name } when the conversation is
 *     opened to focus on a specific exercise (e.g. from a per-exercise card),
 *     so the same thread can carry per-exercise follow-ups.
 *   - focusInsightId: when opened from "View more" on a day's insight card,
 *     the modal scrolls to and highlights that specific insight message in
 *     the thread. Pass `true` to focus the most recent insight.
 *   - focusTimelineEntryId: when opened from a toast, anchors the exact daily
 *     insight, generated activity reply, or activity event that produced it.
 */
const CoachHubModal = ({
    backendService,
    modalService,
    seedMessage,
    seedAttachments,
    exerciseScope,
    focusInsightId,
    focusTimelineEntryId,
    showDailyCoachFailure = false,
    coachIsThinking = false,
    onProposalApprove,
    onProposalDismiss
}) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [conversation, setConversation] = useState([]);
    const [input, setInput] = useState('');
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const transitionOpenedAtRef = useRef(0);
    const [sending, setSending] = useState(false);
    const [dailyCoachThinking, setDailyCoachThinking] = useState(false);
    const [sharedCoachThinking, setSharedCoachThinking] = useState(
        () => Boolean(coachIsThinking || getCoachThinkingSnapshot())
    );
    const [askCoachTyping, setAskCoachTyping] = useState(() => getCoachAskTypingSnapshot());
    const [error, setError] = useState(null);
    const [deliveryNotice, setDeliveryNotice] = useState(null);
    const [regeneratingReplyId, setRegeneratingReplyId] = useState(null);
    const [regenerateReplyError, setRegenerateReplyError] = useState(null);
    const [retryingReviewIds, setRetryingReviewIds] = useState({});
    const [retryReviewErrors, setRetryReviewErrors] = useState({});
    const [expandedWorkoutThreads, setExpandedWorkoutThreads] = useState({});
    const [workoutThreads, setWorkoutThreads] = useState({});
    const [contextOpen, setContextOpen] = useState(false);
    const [openingCoachSettings, setOpeningCoachSettings] = useState(false);
    const [scope, setScope] = useState(
        exerciseScope && exerciseScope.exercise_name
            ? { type: 'exercise', exercise_id: exerciseScope.exercise_id, exercise_name: exerciseScope.exercise_name }
            : null
    );
    const chatEndRef = useRef(null);
    const chatHistoryRef = useRef(null);
    const wasAtBottomRef = useRef(true);
    const previousTimelineTailRef = useRef(null);
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);
    const seedSentRef = useRef(false);
    const pendingCoachMessageRef = useRef(null);
    const reconciliationTimersRef = useRef(new Set());
    const reconciliationControllersRef = useRef(new Set());
    const reconciliationMountedRef = useRef(true);
    const activeAskIdsRef = useRef(new Set());
    const activeExternalRequestsRef = useRef(new Map());
    const settledExternalRequestsRef = useRef(new Set());
    const didInitialScrollRef = useRef(false);
    const focusEntryRef = useRef(null);
    const [insightHighlight, setInsightHighlight] = useState(false);
    const [newMessagesPending, setNewMessagesPending] = useState(false);
    const openCoachOwnerSettings = async () => {
        if (!modalService?.open || openingCoachSettings) return;
        setOpeningCoachSettings(true);
        setError(null);
        try {
            const status = await backendService.fetchExternalCoachAgent();
            if (status?.settings?.scope === 'dojo') {
                const { default: DojoSettingsModal } = await import('./DojoSettingsModal');
                modalService.open(DojoSettingsModal, { backendService });
            } else {
                const { default: UserPreferencesModal } = await import('./UserPreferencesModal');
                modalService.open(UserPreferencesModal, { backendService, scrollToSection: 'ai-coach' });
            }
        } catch (settingsError) {
            setError(settingsError?.message || 'Could not open Coach settings.');
        } finally {
            setOpeningCoachSettings(false);
        }
    };
    const scheduleCoachReconciliation = useCallback((callback, delay) => {
        const timer = window.setTimeout(() => {
            reconciliationTimersRef.current.delete(timer);
            callback();
        }, delay);
        reconciliationTimersRef.current.add(timer);
        return timer;
    }, []);

    const fetchCoachHomeForReconciliation = useCallback(async () => {
        const controller = new AbortController();
        reconciliationControllersRef.current.add(controller);
        try {
            return await backendService.fetchCoachHome(controller.signal);
        } finally {
            reconciliationControllersRef.current.delete(controller);
        }
    }, [backendService]);

    useEffect(() => {
        const reconciliationTimers = reconciliationTimersRef.current;
        const reconciliationControllers = reconciliationControllersRef.current;
        const activeAskIds = activeAskIdsRef.current;
        const activeExternalRequests = activeExternalRequestsRef.current;
        const settledExternalRequests = settledExternalRequestsRef.current;
        reconciliationMountedRef.current = true;
        return () => {
            reconciliationMountedRef.current = false;
            reconciliationTimers.forEach(timer => window.clearTimeout(timer));
            reconciliationTimers.clear();
            reconciliationControllers.forEach(controller => controller.abort());
            reconciliationControllers.clear();
            // The modal owns these process-wide typing IDs. Its cancelled poll
            // cannot release them later, so retire them before another mount.
            activeAskIds.forEach(id => publishCoachAskTyping(id, false));
            activeAskIds.clear();
            activeExternalRequests.clear();
            settledExternalRequests.clear();
        };
    }, []);
    // Briefly flashes a fading highlight on the insight that was just toggled
    // so it's obvious which one expanded/collapsed (especially on collapse).
    const [recentlyToggledId, setRecentlyToggledId] = useState(null);
    useEffect(() => subscribeCoachThinking(setSharedCoachThinking), []);
    useEffect(() => subscribeCoachAskTyping(setAskCoachTyping), []);
    useEffect(() => {
        publishCoachHubOpen(true);
        return () => publishCoachHubOpen(false);
    }, []);
    // Insights are collapsed by default; tapping one (or arriving via "View
    // more") expands that specific insight. Keyed by insight id.
    const [expandedInsights, setExpandedInsights] = useState(() => {
        const initialFocusId = focusTimelineEntryId || focusInsightId;
        return (initialFocusId && initialFocusId !== true) ? { [String(initialFocusId)]: true } : {};
    });
    const toggleInsight = (id, el) => {
        setExpandedInsights((prev) => ({ ...prev, [id]: !prev[id] }));
        // Flash a fading highlight on the toggled insight. Adding the class
        // snaps it bright (transition disabled), then removing it on the next
        // painted frame lets the long CSS transition ease it back to rest, so
        // there's no single-frame snap when the highlight clears.
        setRecentlyToggledId(id);
        requestAnimationFrame(() => requestAnimationFrame(() => setRecentlyToggledId(null)));
        // Keep the toggled insight in view: its top can shift when content
        // above/below grows or shrinks, so re-anchor to it after the DOM
        // updates (two frames to clear React's commit + layout).
        const card = el && el.closest ? el.closest('.coach-hub-insight-msg') : null;
        if (card) {
            requestAnimationFrame(() => requestAnimationFrame(() => {
                card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }));
        }
    };

    // Tap-vs-press detection so tapping an insight toggles it, while a long
    // press or a drag (text selection for copy/paste) does not. Records the
    // pointer-down time and position; the click handler ignores the toggle
    // when the gesture looks like a selection rather than a quick tap.
    const insightPressRef = useRef(null);
    const handleInsightPointerDown = (e) => {
        insightPressRef.current = { t: Date.now(), x: e.clientX, y: e.clientY };
    };
    const handleInsightClick = (e, id, el) => {
        // Don't toggle if the user is selecting text (copy/paste intent).
        const selection = window.getSelection && window.getSelection();
        if (selection && selection.toString().trim().length > 0) return;
        const press = insightPressRef.current;
        insightPressRef.current = null;
        if (press) {
            const heldMs = Date.now() - press.t;
            const moved = Math.hypot(e.clientX - press.x, e.clientY - press.y);
            // Long press (>400ms) or a drag (>10px) is a selection gesture.
            if (heldMs > 400 || moved > 10) return;
        }
        toggleInsight(id, el);
    };

    // Proposal records live on their own endpoint so their status can be
    // refreshed immediately after a member/chief responds without reloading
    // the whole coach conversation.
    const refreshStrategyProposals = useCallback(async () => {
        const proposals = await backendService.fetchStrategyProposals();
        setData((previous) => ({ ...(previous || {}), proposals }));
        return proposals;
    }, [backendService]);

    const retryActivityReview = useCallback(async (event) => {
        const eventId = String(event?._id || '');
        if (!eventId || retryingReviewIds[eventId]) return;
        setRetryingReviewIds((previous) => ({ ...previous, [eventId]: true }));
        setRetryReviewErrors((previous) => ({ ...previous, [eventId]: null }));
        try {
            const result = await backendService.retryCoachActivityEvent(eventId);
            if (result?.event) {
                setData((previous) => ({
                    ...(previous || {}),
                    activity_events: (previous?.activity_events || []).map((item) => (
                        String(item?._id) === eventId ? result.event : item
                    )),
                }));
            }
        } catch (retryError) {
            setRetryReviewErrors((previous) => ({
                ...previous,
                [eventId]: retryError?.message || 'Retry failed. Please try again.',
            }));
        } finally {
            setRetryingReviewIds((previous) => ({ ...previous, [eventId]: false }));
        }
    }, [backendService, retryingReviewIds]);

    const toggleWorkoutThread = useCallback(async (event, options = {}) => {
        const eventId = String(event?._id || '');
        if (!eventId || !event?.activity_id) return;
        if (expandedWorkoutThreads[eventId] && options.retry !== true) {
            setExpandedWorkoutThreads((previous) => ({ ...previous, [eventId]: false }));
            return;
        }
        setExpandedWorkoutThreads((previous) => ({ ...previous, [eventId]: true }));
        const cachedThread = workoutThreads[eventId] || {};
        if (!shouldFetchInlineWorkoutThread({ cached: cachedThread, event, retry: options.retry === true })) return;
        const eventVersion = workoutThreadEventVersion(event);
        setWorkoutThreads((previous) => ({
            ...previous,
            [eventId]: { loading: true, loaded: false, eventVersion, events: [], error: null },
        }));
        try {
            const thread = await backendService.getWorkoutCoachThread(event.activity_id);
            setWorkoutThreads((previous) => ({
                ...previous,
                [eventId]: { loading: false, loaded: true, eventVersion, events: thread?.events || [], error: null },
            }));
        } catch (threadError) {
            setWorkoutThreads((previous) => ({
                ...previous,
                [eventId]: {
                    loading: false,
                    loaded: false,
                    eventVersion,
                    events: [],
                    error: threadError?.message || 'Workout chat could not be loaded.',
                },
            }));
        }
    }, [backendService, expandedWorkoutThreads, workoutThreads]);

    // Initial load: fetch the coach context and actionable proposals together.
    useEffect(() => {
        let active = true;
        setLoading(true);
        Promise.all([
            backendService.fetchCoachHome(),
            backendService.fetchStrategyProposals().catch((proposalError) => {
                console.error('Error fetching strategy proposals:', proposalError);
                return [];
            })
        ]).then(([payload, proposals]) => {
            if (!active) return;
            setData({ ...(payload || {}), proposals });
            setConversation(mergePendingCoachMessage(payload?.conversation || [], pendingCoachMessageRef.current));
            setLoading(false);
        });
        return () => { active = false; };
    }, [backendService]);

    // The shared Activities WebSocket owner forwards a narrow invalidation
    // event so an already-open Coach Hub converges without opening a second
    // socket. Preserve proposal state while refreshing the canonical timeline.
    useEffect(() => {
        let active = true;
        const refreshTimeline = (reviewEventId = null) => {
            const readStartedAt = coachSequenceNow();
            backendService.fetchCoachHome().then((payload) => {
                // Only an exact-subject activity invalidation is attributed;
                // interval/visibility refreshes are not the awaited read.
                if (reviewEventId) {
                    noteCoachCanonicalRead('activity_review', reviewEventId, readStartedAt);
                    const reviewed = (payload?.activity_events || []).find(item => String(item?._id) === reviewEventId);
                    if (reviewed && (reviewed.coach_reaction || reviewed.insight_id)) noteCoachContextMatch('activity_review', reviewEventId);
                }
                if (!active) return;
                setData((previous) => ({
                    ...(payload || {}),
                    proposals: Array.isArray(payload?.proposals) ? payload.proposals : (previous?.proposals || []),
                }));
                setConversation(mergePendingCoachMessage(payload?.conversation || [], pendingCoachMessageRef.current));
            }).catch((refreshError) => {
                console.error('Error refreshing Coach timeline:', refreshError);
            });
        };
        const refreshWhenVisible = (event) => {
            const reviewEventId = event?.detail?.eventId ? String(event.detail.eventId) : null;
            if (document.visibilityState !== 'hidden') refreshTimeline(reviewEventId);
        };
        window.addEventListener('coach-timeline-updated', refreshWhenVisible);
        document.addEventListener('visibilitychange', refreshWhenVisible);
        // Process-local WebSocket registries cannot guarantee delivery when
        // API and socket connections land on different dynos. A low-frequency
        // canonical refresh keeps an open thread convergent without another
        // socket or chat-message polling loop.
        const refreshInterval = setInterval(refreshWhenVisible, 30000);
        return () => {
            active = false;
            window.removeEventListener('coach-timeline-updated', refreshWhenVisible);
            document.removeEventListener('visibilitychange', refreshWhenVisible);
            clearInterval(refreshInterval);
        };
    }, [backendService]);

    // A canonical refresh may beat the reply poll. Only a terminal state or
    // reply for the *same* request can retire its typing ID; a partial/older
    // conversation snapshot is not evidence that the request settled.
    useEffect(() => {
        activeExternalRequestsRef.current.forEach((requestId, askId) => {
            const settled = conversation.some(message => String(message.external_request_id || '') === requestId
                && (message.role === 'coach' || (message.role === 'user'
                    && ['completed', 'failed', 'timedout', 'cancelled', 'invalidated'].includes(message.delivery_status))));
            if (!settled) return;
            settledExternalRequestsRef.current.add(requestId);
            activeExternalRequestsRef.current.delete(askId);
            activeAskIdsRef.current.delete(askId);
            publishCoachAskTyping(askId, false);
        });
    }, [conversation]);

    // When opened via "View more" for the latest insight (focusInsightId ===
    // true), expand that resolved insight once the data has loaded. The
    // explicit-id case is already handled by the initial state.
    useEffect(() => {
        if (focusInsightId !== true) return;
        const list = Array.isArray(data?.insights) ? data.insights : [];
        if (list.length === 0) return;
        const latest = list.reduce((a, b) =>
            (new Date(b.created_at).getTime() > new Date(a.created_at).getTime() ? b : a)
        );
        const id = String(latest._id);
        setExpandedInsights((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    }, [data, focusInsightId]);

    const handleChatHistoryScroll = useCallback(() => {
        const history = chatHistoryRef.current;
        if (!history) return;
        const atBottom = history.scrollHeight - history.scrollTop - history.clientHeight <= 12;
        wasAtBottomRef.current = atBottom;
        if (atBottom) setNewMessagesPending(false);
    }, []);

    const insights = useMemo(() => (Array.isArray(data?.insights) ? data.insights : [])
        .map((ins) => ({ insight: ins, actions: buildCoachInsightActions(ins) }))
        .filter(({ insight, actions }) => coachInsightHasVisibleContent(insight, actions)), [data?.insights]);

    // Position the thread once it loads. After that, only follow a new tail
    // message when the user was already at the bottom; never yank a reader
    // away from older content while activity updates or existing cards change.
    useEffect(() => {
        if (loading) return;
        const nextTimeline = buildCoachTimeline({
            conversation,
            insights,
            activityEvents: Array.isArray(data?.activity_events) ? data.activity_events : [],
            proposals: data?.proposals || [],
        });
        const nextTail = nextTimeline[nextTimeline.length - 1]?.key || null;
        const newTail = previousTimelineTailRef.current !== null
            && nextTail !== previousTimelineTailRef.current;
        const initial = !didInitialScrollRef.current;
        previousTimelineTailRef.current = nextTail;
        const shouldScroll = shouldAutoScrollCoachTimeline({
            initial,
            newTail,
            wasAtBottom: wasAtBottomRef.current,
        });
        if (!shouldScroll) {
            if (newTail) setNewMessagesPending(true);
            return;
        }
        if (!didInitialScrollRef.current) {
            didInitialScrollRef.current = true;
            requestAnimationFrame(() => requestAnimationFrame(() => {
                if ((focusTimelineEntryId || focusInsightId) && focusEntryRef.current && chatHistoryRef.current) {
                    alignCoachTimelineTarget(chatHistoryRef.current, focusEntryRef.current, 8);
                    setInsightHighlight(true);
                    setTimeout(() => setInsightHighlight(false), 2400);
                } else if (chatHistoryRef.current) {
                    chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
                }
            }));
            return;
        }
        if (chatHistoryRef.current) {
            chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
        setNewMessagesPending(false);
    }, [loading, conversation, sending, focusInsightId, focusTimelineEntryId, data?.activity_events, insights, data?.proposals]);

    // Focus the composer as soon as the modal finishes loading so the user
    // can start typing immediately. Skipped when opened via "View more"
    // (focusInsightId): there the user wants to read the expanded insight, so
    // we avoid popping the mobile keyboard and stealing reading space.
    useEffect(() => {
        if (loading || focusInsightId || focusTimelineEntryId) return;
        const id = requestAnimationFrame(() => {
            if (inputRef.current) inputRef.current.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [loading, focusInsightId, focusTimelineEntryId]);

    const sendMessage = useCallback(async (overrideText, overrideAttachments) => {
        const message = (overrideText !== undefined ? overrideText : input).trim();
        const draftAttachments = overrideAttachments !== undefined ? overrideAttachments : pendingAttachments;
        if ((!message && draftAttachments.length === 0) || sending) return;
        const isCurrent = () => reconciliationMountedRef.current;
        const askRequestId = (window.crypto?.randomUUID?.() || `coach-ask-${Date.now()}-${Math.random()}`);
        activeAskIdsRef.current.add(askRequestId);
        const finishTyping = () => {
            activeAskIdsRef.current.delete(askRequestId);
            activeExternalRequestsRef.current.delete(askRequestId);
            publishCoachAskTyping(askRequestId, false);
        };
        publishCoachAskTyping(askRequestId, true);

        setInput('');
        setPendingAttachments([]);
        setError(null);
        setRegenerateReplyError(null);
        // Optimistically render the user message and pending attachments.
        const optimisticMessage = {
            role: 'user',
            text: message,
            created_at: new Date().toISOString(),
            client_pending: true,
            attachments: draftAttachments.map((f) => ({
                name: f.name,
                mime_type: f.type || 'application/octet-stream',
                size: f.size || 0,
                kind: (f.type || '').startsWith('image/') ? 'image' : 'file',
                uploading: true
            }))
        };
        pendingCoachMessageRef.current = optimisticMessage;
        setConversation(prev => [...prev, optimisticMessage]);
        setSending(true);
        let keepAskTypingForLateReply = false;
        try {
            let uploaded = [];
            if (draftAttachments.length > 0) {
                uploaded = await backendService.uploadCoachAttachments(draftAttachments);
            }
            if (!isCurrent()) { finishTyping(); return; }
            const result = await backendService.askCoach(message, uploaded.map((a) => a.id), scope || null, askRequestId);
            if (!isCurrent()) { finishTyping(); return; }
            pendingCoachMessageRef.current = null;
            if (result?.conversation) {
                setConversation(result.conversation);
            } else if (result?.reply) {
                setConversation(prev => [...prev, { role: 'coach', text: result.reply, actions: result.actions || [], created_at: new Date().toISOString() }]);
            }
            if (result?.queued && result?.request?.id) {
                keepAskTypingForLateReply = true;
                setDeliveryNotice(null);
                const externalRequestId = String(result.request.id);
                activeExternalRequestsRef.current.set(askRequestId, externalRequestId);
                const reconcileExternalReply = async (attempt = 0) => {
                    if (!isCurrent()) return;
                    try {
                        const payload = await fetchCoachHomeForReconciliation();
                        if (!isCurrent()) return;
                        const canonicalConversation = payload?.conversation || [];
                        // A newer timeline refresh already settled this request.
                        // Never let an older in-flight poll restore queued UI.
                        if (settledExternalRequestsRef.current.has(externalRequestId)) return;
                        const requestMessage = canonicalConversation.find(item => String(item?.external_request_id || '') === externalRequestId && item?.role === 'user');
                        const completed = canonicalConversation.some(item => String(item?.external_request_id || '') === externalRequestId && item?.role === 'coach');
                        setData(previous => ({
                            ...(payload || {}),
                            proposals: Array.isArray(payload?.proposals) ? payload.proposals : (previous?.proposals || []),
                        }));
                        setConversation(canonicalConversation);
                        if (completed) {
                            finishTyping();
                            setError(null);
                            return;
                        }
                        if (['failed', 'timedout'].includes(requestMessage?.delivery_status)) {
                            finishTyping();
                            setError(externalCoachFailureGuidance(requestMessage));
                            return;
                        }
                    } catch (refreshError) {
                        console.error('Error polling external Coach reply:', refreshError);
                    }
                    if (!isCurrent()) return;
                    if (attempt < 119) scheduleCoachReconciliation(() => reconcileExternalReply(attempt + 1), 2000);
                    else {
                        finishTyping();
                        setDeliveryNotice('Your external agent is taking longer than usual. You can close this view and check back later.');
                    }
                };
                scheduleCoachReconciliation(() => reconcileExternalReply(0), 1000);
            }
        } catch (err) {
            if (!isCurrent()) { finishTyping(); return; }
            if (shouldPreserveCoachMessageOnError(err)) {
                keepAskTypingForLateReply = true;
                // A gateway timeout does not cancel the dyno. Keep the optimistic
                // message visible and reconcile the late persisted reply instead
                // of making the user's message disappear and inviting a duplicate retry.
                setError('Warden is still finishing this request. Checking for the saved reply…');
                const reconcileLateReply = async (attempt = 0) => {
                    if (!isCurrent()) return;
                    try {
                        const payload = await fetchCoachHomeForReconciliation();
                        if (!isCurrent()) return;
                        const canonicalConversation = payload?.conversation || [];
                        const completed = hasCoachReplyAfterMessage(canonicalConversation, message);
                        setData((previous) => ({
                            ...(payload || {}),
                            proposals: Array.isArray(payload?.proposals) ? payload.proposals : (previous?.proposals || []),
                        }));
                        setConversation(mergePendingCoachMessage(canonicalConversation, pendingCoachMessageRef.current));
                        if (completed) {
                            pendingCoachMessageRef.current = null;
                            finishTyping();
                            setError(null);
                            return;
                        }
                    } catch (refreshError) {
                        console.error('Error reconciling late Coach reply:', refreshError);
                    }
                    if (!isCurrent()) return;
                    if (attempt < 5) scheduleCoachReconciliation(() => reconcileLateReply(attempt + 1), 5000);
                    else finishTyping();
                };
                scheduleCoachReconciliation(() => reconcileLateReply(0), 5000);
            } else {
                pendingCoachMessageRef.current = null;
                setError(err.message || 'Coach unavailable. Try again.');
                // Roll back only failures known not to have reached persistence.
                setConversation(prev => prev.slice(0, -1));
                setInput(message);
                setPendingAttachments(draftAttachments);
            }
        }
        if (!keepAskTypingForLateReply) finishTyping();
        setSending(false);
    }, [input, pendingAttachments, sending, backendService, scope, scheduleCoachReconciliation, fetchCoachHomeForReconciliation]);

    const handleAsk = useCallback(() => sendMessage(), [sendMessage]);

    const regenerateReply = useCallback(async (message) => {
        const targetId = coachReplyIdentity(message);
        if (!targetId || sending || regeneratingReplyId) return;
        const isCurrent = () => reconciliationMountedRef.current;
        setRegeneratingReplyId(targetId);
        setRegenerateReplyError(null);
        setSending(true);
        try {
            const result = await backendService.regenerateCoachReply(targetId);
            if (!isCurrent()) return;
            if (Array.isArray(result?.conversation)) {
                setConversation(result.conversation);
            } else if (result?.reply) {
                setConversation((previous) => previous.map((item) => (
                    coachReplyIdentity(item) === targetId ? result.reply : item
                )));
            }
        } catch (regenerationError) {
            if (!isCurrent()) return;
            setRegenerateReplyError(regenerationError?.message || 'Could not regenerate this response.');
            try {
                const canonical = await backendService.fetchCoachHome();
                if (!isCurrent()) return;
                if (Array.isArray(canonical?.conversation)) setConversation(canonical.conversation);
            } catch (refreshError) {
                console.error('Could not reconcile Coach conversation after regeneration failure:', refreshError);
            }
        } finally {
            if (isCurrent()) {
                setSending(false);
                setRegeneratingReplyId(null);
            }
        }
    }, [backendService, regeneratingReplyId, sending]);

    const [proposalActionId, setProposalActionId] = useState(null);

    const respondToProposal = useCallback(async (proposal, action, comment) => {
        const proposalId = proposal?.id || proposal?._id;
        if (!proposalId || proposalActionId) return;
        console.info('[CoachHub] proposal action started', { proposalId: String(proposalId), action });
        setProposalActionId(String(proposalId));
        setError(null);
        try {
            let result;
            if (action === 'approve') {
                result = await backendService.approveStrategyProposal(proposalId);
            } else if (action === 'reject') {
                result = await backendService.rejectStrategyProposal(proposalId, comment);
            } else if (action === 'acknowledge') {
                result = await backendService.acknowledgeStrategyProposal(proposalId);
            } else {
                result = await backendService.commentOnStrategyProposal(proposalId, comment);
            }
            console.info('[CoachHub] proposal action completed', { proposalId: String(proposalId), action, status: result?.status });
            const resolvedStatus = action === 'approve' ? 'approved' : (action === 'reject' ? 'rejected' : (action === 'acknowledge' ? 'acknowledged' : null));
            // Reconcile immediately so an approved/declined card becomes a
            // read-only history row without waiting for polling. Then refresh
            // from the canonical proposal endpoint; a refresh failure must not
            // turn an already-successful mutation into a false action failure.
            setData((previous) => ({
                ...(previous || {}),
                proposals: (previous?.proposals || []).map((item) => (
                    String(item.id || item._id) === String(proposalId) && resolvedStatus
                        ? { ...item, status: resolvedStatus, updated_at: new Date().toISOString() }
                        : item
                ))
            }));
            try {
                await refreshStrategyProposals();
            } catch (refreshError) {
                console.error('[CoachHub] proposal refresh failed after successful action', refreshError);
            }
            if (action === 'approve' || action === 'reject' || action === 'acknowledge') {
                const approved = action === 'approve';
                const changes = result?.references?.adjustments || [];
                setConversation((previous) => [...previous, {
                    role: 'coach',
                    kind: 'ai_action_trace',
                    text: action === 'acknowledge'
                        ? 'Got it — I’ll reassess once there is enough meal-logging evidence.'
                        : (approved
                            ? (changes.length > 0 ? `Done — ${changes.join(' · ')}` : 'Done — the approved workout-plan change is now active.')
                            : 'Understood — I declined that proposed workout-plan change.'),
                    created_at: new Date().toISOString()
                }]);
            }
            try {
                if (action === 'approve') await onProposalApprove?.(proposal, result);
                if (action === 'reject') await onProposalDismiss?.(proposal, result);
            } catch (callbackError) {
                // The canonical mutation already succeeded. Optional host
                // callbacks must never turn it into a false action failure.
                console.error('[CoachHub] post-action callback failed', callbackError);
            }
        } catch (proposalError) {
            setError(proposalError.message || 'Could not update strategy proposal. Try again.');
        } finally {
            setProposalActionId(null);
        }
    }, [backendService, onProposalApprove, onProposalDismiss, proposalActionId, refreshStrategyProposals]);

    const approveProposal = useCallback((proposal) => respondToProposal(proposal, 'approve'), [respondToProposal]);
    const dismissProposal = useCallback((proposal) => respondToProposal(proposal, 'reject'), [respondToProposal]);
    const acknowledgeProposal = useCallback((proposal) => respondToProposal(proposal, 'acknowledge'), [respondToProposal]);
    const commentOnProposal = useCallback((proposal) => {
        const comment = window.prompt('Comment on this proposal:', createProposalCommentPrefill(proposal));
        if (comment === null || !comment.trim()) return;
        respondToProposal(proposal, 'comment', comment);
    }, [respondToProposal]);

    // Seed hand-off: when launched from the feed's inline reply, post that
    // first message into the real thread once the initial context has loaded
    // (so fetchCoachHome can't clobber it). Runs exactly once.
    useEffect(() => {
        if (loading || seedSentRef.current) return;
        const hasSeed = (seedMessage && seedMessage.trim()) || (Array.isArray(seedAttachments) && seedAttachments.length > 0);
        if (!hasSeed) return;
        seedSentRef.current = true;
        sendMessage(seedMessage || '', Array.isArray(seedAttachments) ? seedAttachments : []);
    }, [loading, seedMessage, seedAttachments, sendMessage]);

    const submitComposer = useCoachComposerSubmit(handleAsk, sending);

    const openAttachmentPicker = () => {
        if (sending) return;
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const onAttachmentChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setPendingAttachments(prev => [...prev, ...files].slice(0, 4));
        // Allow selecting the same file again after removing it.
        e.target.value = '';
    };

    const removePendingAttachment = (idx) => {
        setPendingAttachments(prev => prev.filter((_, i) => i !== idx));
    };

    // Focus the conversation on a specific exercise and bring the composer
    // into view so the user can immediately ask about it.
    const focusExercise = useCallback((memory) => {
        if (!memory || !memory.exercise_name) return;
        setScope({ type: 'exercise', exercise_id: memory.exercise_id, exercise_name: memory.exercise_name });
        setContextOpen(false);
        setTimeout(() => {
            if (inputRef.current) inputRef.current.focus();
        }, 0);
    }, []);

    const openStrategyModal = ({ startNewOnOpen = false } = {}) => {
        const now = Date.now();
        if (!shouldOpenStrategyTransition({ startNewOnOpen, lastOpenedAt: transitionOpenedAtRef.current, now })) return;
        if (startNewOnOpen) transitionOpenedAtRef.current = now;
        modalService.open(StrategyModal, { backendService, modalService, startNewOnOpen });
    };

    const coach = data?.coach || {};
    const coachName = coach.name || 'Your Coach';
    const externalPending = conversation.some(message => message.role === 'user' && message.external_request_id
        && ['queued', 'claimed', 'working', 'fallback_working'].includes(message.delivery_status));
    const showExternalAgent = !scope && (coach.external_agent_enabled === true || externalPending);
    const coachImage = getCoachImageVariantUrl(coach, 'avatar');
    const renderSmallCoachAvatar = (alt = coachName, eager = false) => (
        <CoachAvatar
            imageUrl={coachImage}
            emoji={coach.emoji || '🥋'}
            identitySettled
            alt={alt}
            eager={eager}
        />
    );

    if (loading) {
        return (
            <div className="coach-hub">
                <ModalLoading text="Gathering your coaching context..." />
            </div>
        );
    }

    const strategy = data?.strategy_snapshot;
    const memories = data?.recent_memory || [];
    const pendingProposals = getPendingStrategyProposals(data);

    // All coaching insights (today + past), each rendered as the coach's
    // message for that day. Workout/nutrition pointers reuse the shared
    // action-card shape.


    // Resolve which insight to focus/highlight (by id, or the latest one
    // when `focusInsightId === true`).
    let focusedInsightId = null;
    if (focusInsightId === true && insights.length > 0) {
        focusedInsightId = String(insights.reduce((a, b) =>
            (new Date(b.insight.created_at).getTime() > new Date(a.insight.created_at).getTime() ? b : a)
        ).insight._id);
    } else if (focusInsightId) {
        focusedInsightId = String(focusInsightId);
    }

    const hasInsight = insights.length > 0;
    const regeneratableReply = latestRegeneratableCoachReply(conversation);
    const regeneratableReplyId = coachReplyIdentity(regeneratableReply);

    // Weave chat, deterministic lifecycle events, generated insights, and the
    // full proposal lifecycle into one stable chronological thread. Coach Hub is
    // the durable history of everything discussed and changed with the coach.
    const timeline = buildCoachTimeline({
        conversation,
        insights,
        activityEvents: Array.isArray(data?.activity_events) ? data.activity_events : [],
        proposals: Array.isArray(data?.proposals) ? data.proposals : [],
    });
    const requestedTimelineFocusId = focusTimelineEntryId || focusedInsightId;
    const focusedTimelineEntry = resolveCoachTimelineFocusEntry(timeline, requestedTimelineFocusId);
    const focusedTimelineKey = focusedTimelineEntry?.key || null;
    const hasPendingActivityReview = (Array.isArray(data?.activity_events) ? data.activity_events : [])
        .some((event) => ['pending', 'claimed'].includes(event?.generation_state));
    const showTopLevelThinking = (showDailyCoachFailure && dailyCoachThinking && !hasPendingActivityReview && !regeneratingReplyId)
        || (externalPending && !deliveryNotice && !regeneratingReplyId)
        || (sending && !regeneratingReplyId)
        || (askCoachTyping && !regeneratingReplyId)
        || (sharedCoachThinking && !hasPendingActivityReview && !regeneratingReplyId);

    // Compute the index of the current phase so the roadmap can highlight it.
    let currentPhaseIdx = -1;
    let weeksElapsed = null;
    if (strategy?.phases?.length) {
        const startedAt = strategy.activated_at;
        if (strategy.timeline?.isComplete) {
            weeksElapsed = strategy.timeline.currentWeek;
            currentPhaseIdx = strategy.phases.length;
        } else {
            if (startedAt) {
                weeksElapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
            }
            if (strategy.current_phase && Number.isInteger(strategy.current_phase.index)) {
                currentPhaseIdx = strategy.current_phase.index;
            }
        }
    }

    return (
        <div className="coach-hub coach-hub--chatfirst">
            {/* ─── Collapsible context header ─── */}
            <div className={`coach-hub-context ${contextOpen ? 'open' : ''}`}>
                <button
                    type="button"
                    className="coach-hub-context-bar"
                    onClick={() => setContextOpen((o) => !o)}
                    aria-expanded={contextOpen}
                >
                    <span className="coach-hub-context-bar-icon">{renderSmallCoachAvatar('', true)}</span>
                    <span className="coach-hub-context-bar-text">
                        <span className="coach-hub-context-bar-title">{coachName}{showExternalAgent && <span className="coach-hub-agent-cue">External agent</span>}</span>
                        <span className="coach-hub-context-bar-sub">
                            {strategy?.primary_goal || 'Strategy · insight · memory'}
                        </span>
                    </span>
                    <span className={`coach-hub-context-chevron ${contextOpen ? 'open' : ''}`}>⌄</span>
                </button>

                {contextOpen && (
                  <div className="coach-hub-context-body">
            <div className="coach-hub-owner-settings">
                <button type="button" className="coach-hub-link" disabled={openingCoachSettings} onClick={openCoachOwnerSettings}>
                    {openingCoachSettings ? 'Opening Coach settings…' : 'Open Coach settings →'}
                </button>
            </div>
            {/* ─── Strategy snapshot ─── */}
            <section className="coach-hub-section">
                <div className="coach-hub-section-header">
                    <h3 className="coach-hub-section-title">🗺️ Your Strategy</h3>
                    {strategy && (
                        <button className="coach-hub-link" onClick={openStrategyModal}>
                            Open full strategy →
                        </button>
                    )}
                </div>
                {strategy ? (
                    <div className="coach-hub-strategy">
                        <div className="coach-hub-strategy-goal">{strategy.primary_goal || 'Your strategy'}</div>
                        {strategy.summary && (
                            <div className="coach-hub-strategy-summary">{strategy.summary}</div>
                        )}
                        {strategy.phases?.length > 0 && (
                            <StrategyRoadmap
                                phases={strategy.phases}
                                currentPhaseIndex={currentPhaseIdx}
                                weeksElapsed={weeksElapsed}
                            />
                        )}
                    </div>
                ) : (
                    <div className="coach-hub-empty">
                        <p>You don't have an active strategy yet. A strategy gives the coach a long-term plan to align every recommendation with.</p>
                        <button className="coach-hub-btn primary" onClick={openStrategyModal}>
                            Build a strategy
                        </button>
                    </div>
                )}
            </section>

            {/* ─── Recent per-exercise coaching memory ─── */}
            <section className="coach-hub-section">
                <div className="coach-hub-section-header">
                    <h3 className="coach-hub-section-title">🧠 What I Told You Last Time</h3>
                </div>
                {memories.length > 0 ? (
                    <div className="coach-hub-memory-list">
                        {memories.map((m, i) => (
                            <div key={`${m.exercise_id}-${i}`} className="coach-hub-memory-card">
                                <div className="coach-hub-memory-header">
                                    <span className="coach-hub-memory-exercise">{m.exercise_name}</span>
                                    {m.created_at && (
                                        <span className="coach-hub-memory-date">
                                            {new Date(m.created_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                                {m.last_session_summary && (
                                    <div className="coach-hub-memory-summary">{m.last_session_summary}</div>
                                )}
                                {m.next_session_targets && (
                                    <div className="coach-hub-memory-targets">
                                        <span className="coach-hub-memory-targets-label">Next session:</span>
                                        {[
                                            m.next_session_targets.weight && `${m.next_session_targets.weight} lbs`,
                                            m.next_session_targets.sets && m.next_session_targets.reps && `${m.next_session_targets.sets}×${m.next_session_targets.reps}`,
                                            m.next_session_targets.intensity
                                        ].filter(Boolean).join(' · ')}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="coach-hub-memory-ask"
                                    onClick={() => focusExercise(m)}
                                >
                                    💬 Ask about {m.exercise_name}
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="coach-hub-empty subtle">
                        <p>Once you complete workouts, the coach's per-exercise notes will appear here so you both stay on the same page.</p>
                    </div>
                )}
            </section>
                  </div>
                )}
            </div>

            {/* ─── Conversation thread (primary surface) ─── */}
            <div className="coach-hub-ask">
                <div
                    className="coach-hub-chat coach-hub-thread"
                    ref={chatHistoryRef}
                    onScroll={handleChatHistoryScroll}
                >
                    {newMessagesPending && (
                        <button
                            type="button"
                            className="coach-hub-new-messages-hint"
                            onClick={() => {
                                if (chatHistoryRef.current) chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
                                setNewMessagesPending(false);
                            }}
                        >
                            New Coach activity ↓
                        </button>
                    )}
                    {conversation.length === 0 && !hasInsight && !(data?.activity_events?.length > 0) && pendingProposals.length === 0 && !sending && (
                        <div className="coach-hub-chat-empty">
                            <p>Ask anything — your coach knows your strategy, recent workouts, and what they told you last time.</p>
                            <div className="coach-hub-chat-suggestions">
                                {[
                                    'Why am I on the current phase?',
                                    'How am I tracking against my goals?',
                                    'What should I focus on this week?'
                                ].map((q) => (
                                    <button
                                        key={q}
                                        className="coach-hub-chat-suggestion"
                                        onClick={() => setInput(q)}
                                        disabled={sending}
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {timeline.map((item) => {
                        if (item.kind === 'activity_event') {
                            const event = item.event || {};
                            const presentation = getActivityEventPresentation(event);
                            const { completed, dayClosing, reopened, side, showCoachAvatar, icon, label, awaitingCoachReply, coachReplyFailed, canRetryReview, selectedMeal, reaction } = presentation;
                            const summary = event.summary || {};
                            const eventId = String(event._id || item.key);
                            const canExpandWorkoutThread = completed
                                && event.activity_type === 'workout'
                                && Boolean(event.activity_id);
                            const workoutThread = workoutThreads[eventId] || {};
                            const details = [
                                summary.completed_set_count > 0 && `${summary.completed_set_count} sets`,
                                summary.total_volume > 0 && `${Math.round(summary.total_volume).toLocaleString()} volume`,
                                summary.calories > 0 && `${Math.round(summary.calories)} kcal`,
                                summary.protein_g > 0 && `${Math.round(summary.protein_g)}g protein`,
                                summary.measurement_count > 0 && `${summary.measurement_count} measurements`,
                                summary.media_count > 0 && `${summary.media_count} files`,
                            ].filter(Boolean);
                            return (
                                <React.Fragment key={item.key}>
                                    <div
                                        ref={item.key === focusedTimelineKey ? focusEntryRef : null}
                                        className={`coach-hub-chat-row ${side} coach-hub-chat-row--activity-event ${completed ? 'completed' : (reopened ? 'reopened' : 'expired')} ${reaction ? 'has-reaction' : ''}${expandedWorkoutThreads[eventId] ? ' has-inline-thread' : ''}`}
                                    >
                                        {showCoachAvatar && (
                                            <div className="coach-hub-chat-avatar">{renderSmallCoachAvatar()}</div>
                                        )}
                                        <div className={`coach-hub-chat-msg ${side} coach-hub-activity-event-msg`}>
                                            <div className="coach-hub-activity-event-label">
                                                <span aria-hidden>{icon}</span>
                                                {label} — {event.activity_name || 'Activity'}
                                            </div>
                                            {details.length > 0 && <div className="coach-hub-activity-event-details">{details.join(' · ')}</div>}
                                            {reaction && (
                                                <CoachReactionChip
                                                    reaction={reaction}
                                                    coachName={coachName}
                                                    outputRef={element => { if (element) noteCoachOutputCommitted('activity_review', eventId, element, 'hub_chip'); }}
                                                />
                                            )}
                                            {canExpandWorkoutThread && (
                                                <button
                                                    type="button"
                                                    className="coach-hub-workout-thread-toggle"
                                                    aria-expanded={Boolean(expandedWorkoutThreads[eventId])}
                                                    aria-controls={`coach-hub-workout-thread-${eventId}`}
                                                    onClick={() => toggleWorkoutThread(event)}
                                                >
                                                    <span>{expandedWorkoutThreads[eventId] ? 'Hide workout chat' : 'View workout chat'}</span>
                                                    <span aria-hidden>{expandedWorkoutThreads[eventId] ? '▴' : '▾'}</span>
                                                </button>
                                            )}
                                            {canExpandWorkoutThread && expandedWorkoutThreads[eventId] && (
                                                <div
                                                    id={`coach-hub-workout-thread-${eventId}`}
                                                    className="coach-hub-workout-thread-inline"
                                                >
                                                    {workoutThread.loading ? (
                                                        <div className="coach-hub-workout-thread-loading" role="status">Loading workout chat…</div>
                                                    ) : workoutThread.error ? (
                                                        <div className="coach-hub-workout-thread-error" role="alert">
                                                            <span>{workoutThread.error}</span>
                                                            <button type="button" onClick={() => toggleWorkoutThread(event, { retry: true })}>Retry</button>
                                                        </div>
                                                    ) : (
                                                        <WorkoutCoachInlineThread
                                                            events={workoutThread.events || []}
                                                            coachName={coachName}
                                                            coachEmoji={coach.emoji || '🥋'}
                                                            coachImage={coachImage}
                                                            backendService={backendService}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {item.ts > 0 && <div className="coach-hub-chat-time">{formatMsgTime(event.occurred_at || event.created_at)}</div>}
                                    </div>
                                    {awaitingCoachReply && (
                                        <div className="coach-hub-chat-row coach coach-hub-chat-row--event-reply-pending">
                                            <div className="coach-hub-chat-avatar">{renderSmallCoachAvatar()}</div>
                                            <div
                                                className="coach-hub-chat-msg coach typing coach-hub-event-reviewing"
                                                role="status"
                                                aria-live="polite"
                                                aria-label={dayClosing ? `${coachName} is preparing your daily wrap-up` : `${coachName} is reviewing this activity`}
                                            >
                                                <span className="coach-hub-event-reviewing-dots" aria-hidden="true">
                                                    <span className="coach-hub-typing-dot" />
                                                    <span className="coach-hub-typing-dot" />
                                                    <span className="coach-hub-typing-dot" />
                                                </span>
                                                <span className="coach-hub-event-review-status">{event.generation_state === 'pending' ? `${coachName} queued this review` : `${coachName} is reviewing this`}</span>
                                            </div>
                                        </div>
                                    )}
                                    {coachReplyFailed && (
                                        <div className="coach-hub-chat-row coach coach-hub-chat-row--event-reply-failed">
                                            <div className="coach-hub-chat-avatar">{renderSmallCoachAvatar()}</div>
                                            <div className="coach-hub-chat-msg coach coach-hub-event-review-failed">
                                                <div>{dayClosing ? `${coachName} couldn't finish ${event.summary?.day_closure?.day_key ? `the ${event.summary.day_closure.day_key}` : 'this day’s'} wrap-up.` : `${coachName} couldn't review this activity.`}</div>
                                                {dayClosing && event.routing === 'external' && <div>{canRetryReview
                                                    ? `Retry reviews this same day with ${coachName} using its latest saved activity data.`
                                                    : 'Retry is not available for this wrap-up.'}</div>}
                                                {selectedMeal && <div>{canRetryReview
                                                    ? 'The selected Coach did not complete this meal review. Check that the same Coach worker is connected, then retry this same meal review. It will not switch to hosted generation.'
                                                    : 'The selected Coach did not complete this meal review. Retry is unavailable if routing, credential, sharing, source or Clear authority changed, or if this failure is not retryable. This review will not switch to hosted generation.'}</div>}
                                                {canRetryReview && (
                                                    <button
                                                        type="button"
                                                        className="coach-hub-retry-review"
                                                        disabled={Boolean(retryingReviewIds[String(event._id)])}
                                                        aria-label={dayClosing && event.summary?.day_closure?.day_key ? `Retry wrap-up for ${event.summary.day_closure.day_key}` : undefined}
                                                        onClick={() => retryActivityReview(event)}
                                                    >
                                                        {retryingReviewIds[String(event._id)] ? 'Retrying…' : (dayClosing ? 'Retry wrap-up' : 'Retry review')}
                                                    </button>
                                                )}
                                                {retryReviewErrors[String(event._id)] && (
                                                    <div className="coach-hub-retry-review-error" role="alert">
                                                        {retryReviewErrors[String(event._id)]}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        }

                        if (item.kind === 'proposal') {
                            const proposal = item.proposal;
                            const proposalId = String(proposal.id || proposal._id);
                            if (item.actionable) {
                                const workoutProposal = ['dojo_workout_plan_adjustment', 'member_workout_plan_adjustment'].includes(proposal.kind);
                                const annotationCount = workoutProposal ? countWorkoutProposalAnnotations(proposal) : 0;
                                return (
                                    <React.Fragment key={item.key}>
                                        {workoutProposal && (
                                            <div className="coach-hub-chat-row coach coach-hub-proposal-annotation-summary">
                                                <div className="coach-hub-chat-avatar">{renderSmallCoachAvatar()}</div>
                                                <div className="coach-hub-chat-msg coach">
                                                    I have {annotationCount || 'some'} exercise-change suggestion{annotationCount === 1 ? '' : 's'}. I’ve annotated them on the affected workout exercises so you can review them in context as you train.
                                                </div>
                                            </div>
                                        )}
                                        <CoachStrategyProposalCard
                                            proposal={proposal}
                                            onApprove={approveProposal}
                                            onDismiss={dismissProposal}
                                            onAcknowledge={acknowledgeProposal}
                                            onComment={commentOnProposal}
                                            disabled={sending || proposalActionId === proposalId}
                                        />
                                    </React.Fragment>
                                );
                            }
                            const resolvedLabel = item.status === 'approved' ? '✅ Approved' : (item.status === 'acknowledged' ? '✅ Acknowledged' : '🚫 Declined');
                            const presentation = presentStrategyProposal(proposal);
                            return (
                                <div key={item.key} className={`coach-hub-chat-row coach coach-hub-chat-row--proposal-history proposal-${item.status}`}>
                                    <div className="coach-hub-chat-avatar">{renderSmallCoachAvatar()}</div>
                                    <div className="coach-hub-chat-msg coach coach-hub-proposal-history-msg">
                                        <div className="coach-hub-proposal-history-label">{resolvedLabel} — {presentation.title}</div>
                                        {presentation.reason && <div className="coach-hub-proposal-history-reason">{presentation.reason}</div>}
                                    </div>
                                    {item.ts > 0 && <div className="coach-hub-chat-time">{formatMsgTime(proposal.updated_at || proposal.created_at)}</div>}
                                </div>
                            );
                        }

                        if (item.kind === 'insight') {
                            const { isActivityReply } = getInsightPresentation(item.insight);
                            if (isActivityReply) {
                                const reply = getInsightTeaser(item.insight);
                                return (
                                    <div
                                        key={item.key}
                                        ref={item.key === focusedTimelineKey ? focusEntryRef : null}
                                        className="coach-hub-chat-row coach coach-hub-chat-row--activity-reply"
                                    >
                                        <div className="coach-hub-chat-avatar">{renderSmallCoachAvatar()}</div>
                                        <div className="coach-hub-chat-msg coach coach-hub-activity-reply-msg">
                                            <div>{reply}</div>
                                            {item.actions.length > 0 && <CoachActionCards actions={item.actions} backendService={backendService} />}
                                        </div>
                                        {item.ts > 0 && <div className="coach-hub-chat-time">{formatMsgTime(item.insight.created_at)}</div>}
                                    </div>
                                );
                            }
                            const isFocused = item.key === focusedTimelineKey;
                            const isExpanded = !!expandedInsights[item.id];
                            const teaser = getInsightTeaser(item.insight);
                            const transitionAction = getStrategyTransitionAction(
                                item.insight,
                                item.insight.strategy_context,
                                strategy
                            );
                            return (
                                <div
                                    key={item.key}
                                    ref={item.key === focusedTimelineKey ? focusEntryRef : null}
                                    className="coach-hub-chat-row coach coach-hub-chat-row--insight"
                                >
                                    <div
                                        className={`coach-hub-chat-msg coach coach-hub-insight-msg ${isExpanded ? 'expanded' : 'collapsed'} ${isFocused && insightHighlight ? 'highlight' : ''} ${item.id === recentlyToggledId ? 'just-toggled' : ''}`}
                                        onPointerDown={handleInsightPointerDown}
                                        onClick={(e) => handleInsightClick(e, item.id, e.currentTarget)}
                                    >
                                        <div className="coach-hub-insight-msg-label">
                                            ✨ {item.insight.kind === 'activity_update' ? 'Coach update' : 'Daily insight'}
                                        </div>
                                        {isExpanded ? (
                                            <>
                                                {item.insight.general_advice && <div>{item.insight.general_advice}</div>}
                                                {item.actions.length > 0 && <CoachActionCards actions={item.actions} backendService={backendService} />}
                                                {item.insight.strategy_assessment?.status_summary && (
                                                    <div className="coach-hub-insight-msg-assessment">
                                                        {item.insight.strategy_assessment.status_summary}
                                                    </div>
                                                )}
                                                {item.insight.strategy_nudge && (
                                                    <div className="coach-hub-insight-msg-assessment">{item.insight.strategy_nudge}</div>
                                                )}
                                                {transitionAction && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={`coach-hub-btn primary coach-hub-strategy-transition-btn ${transitionAction.completed ? 'completed' : ''}`}
                                                            disabled={transitionAction.completed}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                if (!transitionAction.completed) {
                                                                    openStrategyModal({ startNewOnOpen: transitionAction.startNewOnOpen });
                                                                }
                                                            }}
                                                        >
                                                            {transitionAction.label}
                                                        </button>
                                                        {transitionAction.completed && (
                                                            <div className="coach-hub-strategy-transition-complete" role="status">
                                                                ✓ You followed through — your next Strategy & Plan is active.
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                                <div className="coach-hub-insight-toggle">Show less</div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="coach-hub-insight-msg-teaser">{teaser}</div>
                                                <div className="coach-hub-insight-toggle">View more</div>
                                            </>
                                        )}
                                    </div>
                                    {item.ts > 0 && (
                                        <div className="coach-hub-chat-time">{formatMsgTime(item.insight.created_at)}</div>
                                    )}
                                </div>
                            );
                        }

                        const msg = item.msg;
                        const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
                        const actions = Array.isArray(msg.actions) ? msg.actions : [];
                        const targetId = coachReplyIdentity(msg);
                        const canRegenerate = msg.role === 'coach' && msg.source !== 'external_agent' && targetId && targetId === regeneratableReplyId;
                        const isRegeneratingReply = regeneratingReplyId === targetId;
                        const attribution = msg.source === 'external_agent'
                            ? `External agent · ${msg.agent_name || 'Coach agent'}`
                            : msg.source === 'hosted_fallback' ? 'Hosted fallback chosen by you' : 'Hosted Coach';
                        return (
                            <div key={item.key} className={`coach-hub-chat-row ${msg.role}`}>
                                    {msg.role === 'coach' && <div className="coach-hub-chat-avatar">{renderSmallCoachAvatar()}</div>}
                                    <div className={`coach-hub-chat-msg ${msg.role}`}>
                                    {isRegeneratingReply && (
                                        <div className="coach-hub-regeneration-cue" role="status" aria-live="polite" aria-label="Regenerating Coach response">
                                            <span className="coach-hub-regeneration-dots" aria-hidden="true">
                                                <span className="coach-hub-typing-dot" />
                                                <span className="coach-hub-typing-dot" />
                                                <span className="coach-hub-typing-dot" />
                                            </span>
                                            <span>Regenerating response…</span>
                                        </div>
                                    )}
                                    {!isRegeneratingReply && msg.text ? (msg.role === 'coach'
                                        ? <CoachMessageContent text={msg.text} />
                                        : <div>{msg.text}</div>) : null}
                                    {msg.role === 'user' && msg.external_request_id && ['failed', 'timedout'].includes(msg.delivery_status) && (
                                        <div className={`coach-hub-external-delivery status-${msg.delivery_status || 'queued'}`}>
                                            <span>{externalCoachFailureGuidance(msg)}</span>
                                            {['failed', 'timedout'].includes(msg.delivery_status) && <div className="coach-hub-external-delivery-actions">
                                                <button type="button" onClick={async () => {
                                                    const isCurrent = () => reconciliationMountedRef.current;
                                                    try {
                                                        await backendService.retryExternalCoachRequest(msg.external_request_id);
                                                        if (!isCurrent()) return;
                                                        const payload = await backendService.fetchCoachHome();
                                                        if (!isCurrent()) return;
                                                        setConversation(payload?.conversation || []);
                                                        setError(null);
                                                        setDeliveryNotice(null);
                                                    } catch (nextError) { if (isCurrent()) setError(nextError.message); }
                                                }}>Retry external</button>
                                                <button type="button" onClick={async () => {
                                                    const isCurrent = () => reconciliationMountedRef.current;
                                                    try {
                                                        const payload = await backendService.useHostedCoachFallback(msg.external_request_id);
                                                        if (!isCurrent()) return;
                                                        setConversation(payload?.conversation || []);
                                                        setError(null);
                                                    } catch (nextError) { if (isCurrent()) setError(nextError.message); }
                                                }}>Use hosted fallback</button>
                                            </div>}
                                        </div>
                                    )}
                                    {!isRegeneratingReply && actions.length > 0 && (
                                        <CoachActionCards actions={actions} backendService={backendService} />
                                    )}
                                    {!isRegeneratingReply && attachments.length > 0 && (
                                        <div className="coach-hub-msg-attachments">
                                            {attachments.map((att, aIdx) => {
                                                const isImage = (att?.kind === 'image') || (att?.mime_type || '').startsWith('image/');
                                                const name = att?.name || `Attachment ${aIdx + 1}`;
                                                return (
                                                    <div key={`${aIdx}-${name}`} className="coach-hub-msg-attachment">
                                                        {isImage && !att?.uploading ? (
                                                            <ExerciseCoachMessageAttachments attachments={[att]} backendService={backendService} />
                                                        ) : null}
                                                        <div className="coach-hub-msg-attachment-meta">
                                                            {att?.uploading ? 'Uploading… ' : ''}{name}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {canRegenerate && !isRegeneratingReply && (
                                        <div className="coach-hub-reply-actions" aria-live="polite">
                                            <button
                                                type="button"
                                                className="coach-hub-regenerate-reply"
                                                aria-label={regeneratingReplyId === targetId ? 'Regenerating Coach response' : 'Regenerate this Coach response'}
                                                aria-busy={regeneratingReplyId === targetId}
                                                title="Regenerate response"
                                                disabled={sending || Boolean(regeneratingReplyId)}
                                                onClick={() => regenerateReply(msg)}
                                            >
                                                <span aria-hidden="true">↻</span>{' '}
                                                {regeneratingReplyId === targetId ? 'Regenerating…' : 'Regenerate'}
                                            </button>
                                            {regenerateReplyError && !regeneratingReplyId && (
                                                <span className="coach-hub-regenerate-error" role="alert">{regenerateReplyError}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {(msg.created_at || msg.role === 'coach') && (
                                    <div className="coach-hub-chat-time coach-hub-message-meta">
                                        {msg.created_at && <span>{formatMsgTime(msg.created_at)}</span>}
                                        {msg.role === 'coach' && !isRegeneratingReply && (
                                            <details className="coach-hub-attribution">
                                                <summary aria-label={attribution} title={attribution}>ⓘ</summary>
                                                <span>{attribution}</span>
                                            </details>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {showDailyCoachFailure && <DailyCoachFailureInChat backendService={backendService} onThinkingChange={setDailyCoachThinking} />}
                    {showTopLevelThinking && (
                        <div className="coach-hub-chat-row coach coach-hub-chat-row--shared-thinking">
                            <div className="coach-hub-chat-avatar">{renderSmallCoachAvatar()}</div>
                            <div className="coach-hub-chat-msg coach typing coach-hub-shared-thinking" aria-label={`${coachName} is typing`}>
                                <span className="coach-hub-typing-dot" />
                                <span className="coach-hub-typing-dot" />
                                <span className="coach-hub-typing-dot" />
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>

                <div className="coach-hub-composer">
                {error && <div className="coach-hub-error" role="alert">{error}</div>}
                {deliveryNotice && externalPending && <div className="coach-hub-delivery-notice" role="status">{deliveryNotice}</div>}

                {scope && scope.type === 'exercise' && (
                    <div className="coach-hub-scope-chip">
                        <span className="coach-hub-scope-chip-icon">🎯</span>
                        <span className="coach-hub-scope-chip-text">
                            Talking about <strong>{scope.exercise_name}</strong>
                        </span>
                        <button
                            type="button"
                            className="coach-hub-scope-chip-remove"
                            onClick={() => setScope(null)}
                            title="Clear focus"
                        >
                            ×
                        </button>
                    </div>
                )}

                {pendingAttachments.length > 0 && (
                    <div className="coach-hub-pending-attachments">
                        {pendingAttachments.map((f, i) => (
                            <div key={`${f.name}-${i}`} className="coach-hub-pending-chip">
                                <span className="coach-hub-pending-chip-name">{f.name}</span>
                                <button
                                    type="button"
                                    className="coach-hub-pending-chip-remove"
                                    onClick={() => removePendingAttachment(i)}
                                    disabled={sending}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="coach-hub-input-row">
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="coach-hub-file-input-hidden"
                        onChange={onAttachmentChange}
                        disabled={sending}
                    />
                    <button
                        type="button"
                        className="coach-hub-attach-btn"
                        onClick={openAttachmentPicker}
                        disabled={sending}
                        title="Attach files"
                    >
                        📎
                    </button>
                    <CoachMessageInput
                        className="coach-hub-input"
                        ref={inputRef}
                        placeholder={scope && scope.type === 'exercise'
                            ? `Ask about ${scope.exercise_name}…`
                            : `Ask ${coachName} (you can attach files)…`}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onSend={submitComposer}
                        disabled={sending}
                    />
                    <button
                        className="coach-hub-send-btn"
                        onClick={submitComposer}
                        disabled={sending || (!input.trim() && pendingAttachments.length === 0)}
                    >
                        {sending ? '…' : 'Send'}
                    </button>
                </div>
                </div>
            </div>
        </div>
    );
};

export default CoachHubModal;
